import React, { useState, useEffect, useRef } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Calendar, Eye, Trash2, FileDown, Send, Paperclip, X, AlertTriangle, Lightbulb, Save, Vote } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import VoteLiveAG from '@/components/VoteLiveAG';

const AssembleesGeneralesV2 = ({ user }) => {
  const [assemblees, setAssemblees] = useState([]);
  const [copropriete, setCopropriete] = useState(null);
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [convocationModal, setConvocationModal] = useState(false);
  const [selectedAG, setSelectedAG] = useState(null);
  const [annexes, setAnnexes] = useState([]);
  const [convocationEditable, setConvocationEditable] = useState('');
  const documentRef = useRef(null);
  const convocationRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [newAG, setNewAG] = useState({
    titre: '',
    date_ag: '',
    heure: '',
    lieu: '',
    president_seance: '',
    secretaire: '',
    scrutateurs: '',
    ordre_du_jour: '',
    resolutions: []
  });
  
  const [isSavingPV, setIsSavingPV] = useState(false);

  // Get role from role_par_copropriete or direct role property
  const userRole = user.role || (user.role_par_copropriete && user.role_par_copropriete[user.copropriete_active_id]);
  const isPresident = userRole === 'président' || user.is_master_admin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agResponse, coproResponse, copropsResponse] = await Promise.all([
        apiClient.get('/assemblees'),
        apiClient.get(`/coproprietes/${user.copropriete_active_id}`),
        apiClient.get('/coproprietaires')
      ]);
      setAssemblees(agResponse.data);
      setCopropriete(coproResponse.data);
      setCoproprietaires(copropsResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAG = async () => {
    if (!newAG.titre || !newAG.date_ag || !newAG.ordre_du_jour) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await apiClient.post('/assemblees', {
        titre: newAG.titre,
        date_ag: new Date(newAG.date_ag).toISOString(),
        heure: newAG.heure,
        lieu: newAG.lieu,
        president_seance: newAG.president_seance,
        secretaire: newAG.secretaire,
        scrutateurs: newAG.scrutateurs,
        ordre_du_jour: newAG.ordre_du_jour,
        president_designe: null
      });
      toast.success('Assemblée générale créée');
      setCreateModal(false);
      setNewAG({ titre: '', date_ag: '', heure: '', lieu: '', president_seance: '', secretaire: '', scrutateurs: '', ordre_du_jour: '', resolutions: [] });
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la création');
      if (message) toast.error(message);
    }
  };

  const openPreview = (ag) => {
    setSelectedAG(ag);
    setPreviewModal(true);
  };

  const openConvocation = (ag) => {
    const dateAG = new Date(ag.date_ag);
    const today = new Date();
    const joursRestants = differenceInDays(dateAG, today);
    
    // La convocation doit être envoyée AU MOINS 21 jours AVANT l'AG
    // Si on est à moins de 21 jours, il est trop tard
    if (joursRestants < 21) {
      toast.error(`Impossible d'envoyer la convocation : l'AG est dans ${joursRestants} jours. Le délai minimum est de 21 jours avant l'AG.`);
      return;
    }
    
    setSelectedAG(ag);
    setAnnexes([]);
    setConvocationModal(true);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    // Filtrer pour n'accepter que les PDF
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length < files.length) {
      toast.warning('Seuls les fichiers PDF sont acceptés pour les annexes');
    }
    
    const newAnnexes = pdfFiles.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      file: file,
      type: file.type
    }));
    setAnnexes(prev => [...prev, ...newAnnexes]);
  };

  const removeAnnexe = (id) => {
    setAnnexes(prev => prev.filter(a => a.id !== id));
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const generateConvocationPDF = async () => {
    if (!selectedAG) return;
    
    setIsGenerating(true);
    
    try {
      // Convertir les annexes en base64
      const annexesBase64 = await Promise.all(
        annexes.map(async (annexe) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result.split(',')[1];
              resolve({
                filename: annexe.name,
                content_base64: base64
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(annexe.file);
          });
        })
      );
      
      // Appeler l'API backend pour générer le PDF fusionné
      const response = await apiClient.post(`/assemblees/${selectedAG.id}/convocation`, {
        annexes: annexesBase64
      });
      
      // Télécharger le PDF fusionné
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.content_base64}`;
      link.download = response.data.filename;
      link.click();
      
      const nbAnnexes = response.data.nb_annexes;
      if (nbAnnexes > 0) {
        toast.success(`Convocation générée avec ${nbAnnexes} annexe(s) fusionnée(s)`);
      } else {
        toast.success('Convocation générée avec succès');
      }
      
      setConvocationModal(false);
      fetchData(); // Rafraîchir pour voir le statut "convoquée"
      
    } catch (error) {
      console.error('Erreur:', error);
      const message = getErrorMessage(error, 'Erreur lors de la génération de la convocation');
      if (message) toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePDF = async () => {
    if (!selectedAG) return;
    
    try {
      // Appeler l'API backend pour générer le PDF du PV
      const response = await apiClient.get(`/assemblees/${selectedAG.id}/pdf`);
      
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.content_base64}`;
      link.download = response.data.filename;
      link.click();
      
      toast.success('PDF du PV généré avec succès');
    } catch (error) {
      // L'erreur 402 est gérée par l'intercepteur global
      const message = getErrorMessage(error, 'Erreur lors de la génération du PDF');
      if (message) toast.error(message);
    }
  };

  const savePVContent = async () => {
    if (!selectedAG || !documentRef.current) return;
    
    setIsSavingPV(true);
    try {
      const pvContent = documentRef.current.innerHTML;
      
      await apiClient.put(`/assemblees/${selectedAG.id}/pv`, {
        pv_content: pvContent
      });
      
      toast.success('PV enregistré avec succès');
      
      // Mettre à jour l'AG sélectionnée avec le nouveau contenu
      setSelectedAG(prev => ({
        ...prev,
        pv_content: pvContent,
        pv_last_modified: new Date().toISOString()
      }));
      
      // Rafraîchir la liste
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de l\'enregistrement');
      toast.error(message);
    } finally {
      setIsSavingPV(false);
    }
  };

  const deleteAG = async (agId) => {
    if (!window.confirm('Supprimer cette assemblée générale ?')) return;

    try {
      await apiClient.delete(`/assemblees/${agId}`);
      toast.success('AG supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getJoursRestants = (dateAG) => {
    const today = new Date();
    const agDate = new Date(dateAG);
    return differenceInDays(agDate, today);
  };

  const canSendConvocation = (dateAG) => {
    const jours = getJoursRestants(dateAG);
    // On peut envoyer si l'AG est dans au moins 21 jours (pas passée et pas trop proche)
    return jours >= 21;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <>
      <div className="fade-in print:hidden" data-testid="assemblees-page">
        <div className="mb-6 md:mb-8 flex flex-col gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Assemblées générales</h1>
            <p className="text-slate-500 text-base md:text-lg">Historique et gestion des AG</p>
          </div>
          {isPresident && (
            <Button
              onClick={() => setCreateModal(true)}
              data-testid="create-ag-button"
              className="bg-slate-900 text-white hover:bg-slate-800 btn-hover w-full sm:w-auto sm:self-start"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Nouvelle AG
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {assemblees.map((ag) => {
            const joursRestants = getJoursRestants(ag.date_ag);
            const canConvoke = canSendConvocation(ag.date_ag);
            const isPast = joursRestants < 0;
            
            return (
              <div
                key={ag.id}
                className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 md:p-6 card-hover"
                data-testid={`ag-card-${ag.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-2">{ag.titre}</h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        {format(new Date(ag.date_ag), 'dd MMMM yyyy', { locale: fr })}
                        {ag.heure && ` à ${ag.heure}`}
                      </span>
                      {!isPast && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          joursRestants < 25 ? 'bg-red-100 text-red-700' :
                          joursRestants <= 30 ? 'bg-orange-100 text-orange-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          J-{joursRestants}
                        </span>
                      )}
                      {isPast && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          Passée
                        </span>
                      )}
                      {ag.convocation_envoyee && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Convoquée
                        </span>
                      )}
                    </div>
                    {ag.lieu && (
                      <p className="text-sm text-slate-500 mt-1">📍 {ag.lieu}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Bouton Vote Live - visible le jour de l'AG ou dans les 7 jours après */}
                    {joursRestants <= 0 && joursRestants >= -7 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                        onClick={() => {
                          const element = document.querySelector(`[data-testid="vote-live-section"]`);
                          if (element) element.scrollIntoView({ behavior: 'smooth' });
                        }}
                        data-testid={`vote-live-btn-${ag.id}`}
                      >
                        <Vote className="w-4 h-4 mr-2" />
                        Vote en direct
                      </Button>
                    )}
                    <Button
                      onClick={() => openPreview(ag)}
                      variant="outline"
                      size="sm"
                      className="btn-hover"
                      data-testid={`preview-ag-${ag.id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Voir PV
                    </Button>
                    {isPresident && !isPast && (
                      <Button
                        onClick={() => openConvocation(ag)}
                        variant="outline"
                        size="sm"
                        className={`btn-hover ${canConvoke ? 'text-green-600 border-green-200 hover:bg-green-50' : 'text-red-600 border-red-200 opacity-60'}`}
                        data-testid={`convocation-ag-${ag.id}`}
                        disabled={!canConvoke}
                        title={canConvoke ? 'Envoyer une convocation' : `Trop tard: moins de 21 jours avant l'AG`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Convocation
                      </Button>
                    )}
                    {isPresident && (
                      <Button
                        onClick={() => deleteAG(ag.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="bg-slate-50 rounded-md p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Ordre du jour</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{ag.ordre_du_jour}</p>
                </div>
                
                {/* Vote Live - affiché si l'AG est aujourd'hui ou dans le passé récent (7 jours) */}
                {joursRestants <= 0 && joursRestants >= -7 && (
                  <VoteLiveAG 
                    ag={ag} 
                    user={user} 
                    isPresident={isPresident}
                    onVoteComplete={() => fetchData()}
                  />
                )}
              </div>
            );
          })}
          {assemblees.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-slate-500">Aucune assemblée générale enregistrée</p>
            </div>
          )}
        </div>

        {/* Create Modal */}
        <Dialog open={createModal} onOpenChange={setCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle assemblée générale</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={newAG.titre}
                  onChange={(e) => setNewAG({ ...newAG, titre: e.target.value })}
                  data-testid="ag-titre-input"
                  placeholder="Ex: AG Ordinaire 2025"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newAG.date_ag}
                    onChange={(e) => setNewAG({ ...newAG, date_ag: e.target.value })}
                    data-testid="ag-date-input"
                  />
                </div>
                <div>
                  <Label>Heure *</Label>
                  <Input
                    value={newAG.heure}
                    onChange={(e) => setNewAG({ ...newAG, heure: e.target.value })}
                    placeholder="14h00"
                  />
                </div>
                <div>
                  <Label>Lieu *</Label>
                  <Input
                    value={newAG.lieu}
                    onChange={(e) => setNewAG({ ...newAG, lieu: e.target.value })}
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Président de séance</Label>
                  <Input
                    value={newAG.president_seance}
                    onChange={(e) => setNewAG({ ...newAG, president_seance: e.target.value })}
                    placeholder="Nom complet"
                  />
                </div>
                <div>
                  <Label>Secrétaire</Label>
                  <Input
                    value={newAG.secretaire}
                    onChange={(e) => setNewAG({ ...newAG, secretaire: e.target.value })}
                    placeholder="Nom complet"
                  />
                </div>
                <div>
                  <Label>Scrutateurs</Label>
                  <Input
                    value={newAG.scrutateurs}
                    onChange={(e) => setNewAG({ ...newAG, scrutateurs: e.target.value })}
                    placeholder="Nom complet"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Ordre du jour *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await apiClient.get('/sujets-proposes/pour-ag');
                        const sujets = response.data.sujets || [];
                        if (sujets.length === 0) {
                          toast.info('Aucun sujet proposé à intégrer');
                          return;
                        }
                        const sujetsText = sujets.map(s => `- ${s.titre}${s.description ? ': ' + s.description : ''}`).join('\n');
                        const currentOrdre = newAG.ordre_du_jour.trim();
                        const separator = currentOrdre ? '\n\n--- Sujets proposés par les copropriétaires ---\n' : '';
                        setNewAG(prev => ({
                          ...prev,
                          ordre_du_jour: currentOrdre + separator + sujetsText
                        }));
                        toast.success(`${sujets.length} sujet(s) ajouté(s) à l'ordre du jour`);
                      } catch (error) {
                        toast.error('Erreur lors du chargement des sujets');
                      }
                    }}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    data-testid="load-sujets-btn"
                  >
                    <Lightbulb className="w-4 h-4 mr-1" />
                    Charger sujets proposés
                  </Button>
                </div>
                <Textarea
                  value={newAG.ordre_du_jour}
                  onChange={(e) => setNewAG({ ...newAG, ordre_du_jour: e.target.value })}
                  data-testid="ag-ordre-input"
                  rows={8}
                  placeholder="Listez les points à discuter (un par ligne)"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleCreateAG}
                  data-testid="save-ag-button"
                  className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
                >
                  Créer l'AG
                </Button>
                <Button
                  onClick={() => setCreateModal(false)}
                  variant="outline"
                  data-testid="cancel-ag-button"
                  className="flex-1"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Modal - PV */}
        <Dialog open={previewModal} onOpenChange={setPreviewModal}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
            <DialogHeader className="print:hidden">
              <DialogTitle>Procès-verbal - {selectedAG?.titre}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-6 print:p-0" id="ag-content">
              {selectedAG && (
                <div ref={documentRef}>
                  <AGDocument 
                    ag={selectedAG} 
                    copropriete={copropriete} 
                    coproprietaires={coproprietaires}
                    user={user}
                    isEditable={true}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-4 border-t print:hidden">
              {selectedAG?.pv_last_modified && (
                <p className="text-xs text-slate-500 text-right">
                  Dernière modification : {format(new Date(selectedAG.pv_last_modified), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                  {selectedAG.pv_modified_by && ` par ${selectedAG.pv_modified_by}`}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button 
                  onClick={savePVContent} 
                  variant="outline"
                  disabled={isSavingPV}
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSavingPV ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
                <Button onClick={generatePDF} className="bg-slate-900 text-white">
                  <FileDown className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Convocation Modal */}
        <Dialog open={convocationModal} onOpenChange={setConvocationModal}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Convocation à l'AG - {selectedAG?.titre}
              </DialogTitle>
            </DialogHeader>
            
            {selectedAG && (
              <>
                {/* Info délai */}
                <div className="p-3 rounded-lg flex items-center gap-2 bg-green-50 text-green-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">
                    L'AG est dans <strong>{getJoursRestants(selectedAG.date_ag)} jours</strong>. 
                    La convocation sera fusionnée avec les annexes en un seul PDF.
                  </span>
                </div>

                {/* Annexes */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Annexes PDF à fusionner</Label>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Ajouter PDF
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                  
                  {annexes.length > 0 ? (
                    <div className="space-y-2">
                      {annexes.map((annexe, index) => (
                        <div key={annexe.id} className="flex items-center justify-between bg-white p-2 rounded border">
                          <span className="text-sm">
                            <strong>Annexe {index + 1}:</strong> {annexe.name}
                          </span>
                          <button 
                            onClick={() => removeAnnexe(annexe.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Aucune annexe PDF. Vous pouvez ajouter des documents (budget, devis, etc.) qui seront fusionnés dans le PDF final.</p>
                  )}
                </div>

                {/* Preview convocation */}
                <div className="flex-1 overflow-y-auto border rounded-lg">
                  <div ref={convocationRef} className="p-6">
                    <ConvocationDocument 
                      ag={selectedAG} 
                      copropriete={copropriete}
                      annexes={annexes}
                      isEditable={true}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setConvocationModal(false)} disabled={isGenerating}>
                    Annuler
                  </Button>
                  <Button 
                    onClick={generateConvocationPDF} 
                    className="bg-slate-900 text-white"
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Génération...
                      </>
                    ) : (
                      <>
                        <FileDown className="w-4 h-4 mr-2" />
                        Générer la convocation {annexes.length > 0 && `(+ ${annexes.length} annexe${annexes.length > 1 ? 's' : ''})`}
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Print version */}
      {previewModal && selectedAG && (
        <div className="hidden print:block">
          <AGDocument 
            ag={selectedAG} 
            copropriete={copropriete} 
            coproprietaires={coproprietaires}
            user={user}
            isEditable={false}
          />
        </div>
      )}
    </>
  );
};

// Composant Convocation
const ConvocationDocument = ({ ag, copropriete, annexes, isEditable }) => {
  const ordreItems = ag.ordre_du_jour?.split('\n').filter(item => item.trim()) || [];
  
  return (
    <div 
      className="bg-white"
      contentEditable={isEditable}
      suppressContentEditableWarning={true}
      style={{ outline: isEditable ? '1px dashed #cbd5e1' : 'none' }}
    >
      {/* En-tête */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">CONVOCATION</h1>
        <h2 className="text-xl font-semibold">ASSEMBLÉE GÉNÉRALE {ag.titre?.toUpperCase().includes('ORDINAIRE') ? 'ORDINAIRE' : 'EXTRAORDINAIRE'}</h2>
        <p className="text-lg mt-2">{copropriete?.nom}</p>
      </div>

      <div className="border-t-2 border-slate-300 my-6"></div>

      {/* Destinataire */}
      <div className="mb-6">
        <p className="text-sm mb-4">Madame, Monsieur,</p>
        <p className="text-sm mb-4">
          Nous avons l'honneur de vous convoquer à l'Assemblée Générale {ag.titre?.toLowerCase().includes('ordinaire') ? 'Ordinaire' : 'Extraordinaire'} des 
          copropriétaires de l'immeuble <strong>{copropriete?.nom}</strong>, sis {copropriete?.adresse}.
        </p>
      </div>

      {/* Informations pratiques */}
      <div className="bg-slate-50 p-4 rounded-lg mb-6">
        <h3 className="text-lg font-bold mb-3">INFORMATIONS PRATIQUES</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p><strong>Date :</strong> {format(new Date(ag.date_ag), 'EEEE dd MMMM yyyy', { locale: fr })}</p>
            <p><strong>Heure :</strong> {ag.heure || '14h00'}</p>
          </div>
          <div>
            <p><strong>Lieu :</strong> {ag.lieu || copropriete?.adresse}</p>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 my-6"></div>

      {/* Ordre du jour */}
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-4">ORDRE DU JOUR</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm">
          <li>Désignation du Président de séance, du Secrétaire et des Scrutateurs</li>
          {ordreItems.map((item, i) => (
            <li key={i}>{item.trim()}</li>
          ))}
          <li>Questions diverses</li>
        </ol>
      </div>

      <div className="border-t border-slate-200 my-6"></div>

      {/* Pouvoir */}
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3">EN CAS D'ABSENCE</h3>
        <p className="text-sm mb-4">
          Si vous ne pouvez pas assister personnellement à cette assemblée, vous avez la possibilité de vous faire 
          représenter par un mandataire de votre choix (copropriétaire ou non) muni d'un pouvoir.
        </p>
        
        <div className="border-2 border-dashed border-slate-300 p-4 rounded-lg bg-slate-50">
          <h4 className="text-center font-bold mb-4">POUVOIR</h4>
          <p className="text-sm mb-2">Je soussigné(e) : ________________________________</p>
          <p className="text-sm mb-2">Propriétaire du lot n° : ________________________________</p>
          <p className="text-sm mb-4">Donne pouvoir à : ________________________________</p>
          <p className="text-sm mb-2">Pour me représenter à l'Assemblée Générale du {format(new Date(ag.date_ag), 'dd/MM/yyyy')} 
            et voter en mon nom sur toutes les résolutions inscrites à l'ordre du jour.</p>
          <div className="flex justify-between mt-6">
            <div>
              <p className="text-sm">Fait à : ________________</p>
              <p className="text-sm mt-2">Le : ________________</p>
            </div>
            <div>
              <p className="text-sm">Signature :</p>
              <div className="w-32 h-16 border-b border-slate-400 mt-8"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Annexes */}
      {annexes.length > 0 && (
        <>
          <div className="border-t border-slate-200 my-6"></div>
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">DOCUMENTS ANNEXES</h3>
            <ul className="list-disc list-inside text-sm space-y-1">
              {annexes.map((annexe, index) => (
                <li key={annexe.id}>Annexe {index + 1} : {annexe.name}</li>
              ))}
            </ul>
          </div>
        </>
      )}

      <div className="border-t border-slate-200 my-6"></div>

      {/* Signature */}
      <div className="text-sm">
        <p className="mb-4">
          Dans l'attente de vous rencontrer lors de cette assemblée, nous vous prions d'agréer, 
          Madame, Monsieur, l'expression de nos salutations distinguées.
        </p>
        <div className="mt-8">
          <p>Le Président du Conseil Syndical</p>
          <p className="text-slate-500 mt-1">{copropriete?.nom}</p>
        </div>
      </div>

      {/* Pied de page */}
      <div className="border-t border-slate-200 mt-8 pt-4">
        <p className="text-xs text-slate-500 text-center">
          Conformément aux dispositions de la loi n° 65-557 du 10 juillet 1965 et du décret n° 67-223 du 17 mars 1967
        </p>
      </div>
    </div>
  );
};

// Composant de document AG (PV)
const AGDocument = ({ ag, copropriete, coproprietaires, user, isEditable = false }) => {
  const ordreItems = ag.ordre_du_jour?.split('\n').filter(item => item.trim()) || [];
  
  // Si un contenu HTML a été sauvegardé, l'utiliser
  if (ag.pv_content && isEditable) {
    return (
      <div 
        className="ag-document bg-white p-8 print:p-12"
        contentEditable={isEditable}
        suppressContentEditableWarning={true}
        style={{ outline: isEditable ? '1px dashed #cbd5e1' : 'none', minHeight: '29.7cm' }}
        dangerouslySetInnerHTML={{ __html: ag.pv_content }}
      />
    );
  }
  
  const generateResolutions = () => {
    const resolutions = [
      {
        numero: 1,
        titre: 'Désignation du Président, du Secrétaire et des Scrutateurs',
        contenu: (
          <>
            <p className="text-sm mb-2">L'Assemblée Générale désigne :</p>
            <ul className="list-disc list-inside text-sm ml-4 mb-2">
              <li>Président de séance : {ag.president_seance || `${user.prenom} ${user.nom}`}</li>
              <li>Secrétaire de séance : {ag.secretaire || 'À désigner'}</li>
              <li>Scrutateurs : {ag.scrutateurs || 'À désigner'}</li>
            </ul>
          </>
        )
      }
    ];

    ordreItems.forEach((item, index) => {
      resolutions.push({
        numero: index + 2,
        titre: item,
        contenu: (
          <p className="text-sm mb-2">
            L'Assemblée Générale approuve la proposition concernant : {item}.
          </p>
        )
      });
    });

    return resolutions;
  };

  const resolutions = generateResolutions();

  return (
    <div 
      className="ag-document bg-white p-8 print:p-12"
      contentEditable={isEditable}
      suppressContentEditableWarning={true}
      style={{ outline: isEditable ? '1px dashed #cbd5e1' : 'none', minHeight: '29.7cm' }}
    >
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">PROCÈS-VERBAL DE L'ASSEMBLÉE GÉNÉRALE</h1>
        <h2 className="text-xl font-semibold">{copropriete?.nom}</h2>
      </div>

      <div className="border-t-2 border-slate-300 my-6"></div>

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3">INFORMATIONS GÉNÉRALES</h3>
        <div className="space-y-1 text-sm">
          <p><strong>Date :</strong> {format(new Date(ag.date_ag), 'dd/MM/yyyy')}</p>
          <p><strong>Heure :</strong> {ag.heure || '14h00'}</p>
          <p><strong>Lieu :</strong> {ag.lieu || copropriete?.adresse}</p>
          <p><strong>Président de séance :</strong> {ag.president_seance || `${user.prenom} ${user.nom}`}</p>
          <p><strong>Secrétaire de séance :</strong> {ag.secretaire || 'À désigner'}</p>
          <p><strong>Scrutateurs :</strong> {ag.scrutateurs || 'À désigner'}</p>
        </div>
      </div>

      <div className="border-t-2 border-slate-300 my-6"></div>

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3">ORDRE DU JOUR</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          {ordreItems.map((item, i) => (
            <li key={i}>{item.trim()}</li>
          ))}
        </ol>
      </div>

      <div className="border-t-2 border-slate-300 my-6"></div>

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-4">RÉSOLUTIONS ADOPTÉES</h3>
        
        {resolutions.map((resolution) => (
          <div key={resolution.numero} className="mb-5">
            <h4 className="font-bold text-sm mb-2">
              Résolution n°{resolution.numero} : {resolution.titre}
            </h4>
            {resolution.contenu}
            <div className="flex gap-4 text-sm mt-2">
              <span>[X] Pour</span>
              <span>[ ] Contre</span>
              <span>[ ] Abstention</span>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t-2 border-slate-300 my-6"></div>

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3">FEUILLE DE PRÉSENCE</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-slate-300 text-sm min-w-[500px]">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-300 px-3 py-2 text-left">N° Lot</th>
              <th className="border border-slate-300 px-3 py-2 text-left">Copropriétaire</th>
              <th className="border border-slate-300 px-3 py-2 text-center">Tantièmes</th>
              <th className="border border-slate-300 px-3 py-2 text-center">Présent</th>
              <th className="border border-slate-300 px-3 py-2 text-center">Représenté</th>
              <th className="border border-slate-300 px-3 py-2 text-center">Absent</th>
            </tr>
          </thead>
          <tbody>
            {coproprietaires.map((copro, i) => (
              <tr key={i}>
                <td className="border border-slate-300 px-3 py-2">{copro.lot || 'N/A'}</td>
                <td className="border border-slate-300 px-3 py-2">{copro.prenom} {copro.nom}</td>
                <td className="border border-slate-300 px-3 py-2 text-center font-medium">{copro.tantiemes || 0}</td>
                <td className="border border-slate-300 px-3 py-2 text-center">[ ]</td>
                <td className="border border-slate-300 px-3 py-2 text-center">[ ]</td>
                <td className="border border-slate-300 px-3 py-2 text-center">[ ]</td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold">
              <td className="border border-slate-300 px-3 py-2"></td>
              <td className="border border-slate-300 px-3 py-2">TOTAL</td>
              <td className="border border-slate-300 px-3 py-2 text-center">
                {coproprietaires.reduce((sum, c) => sum + (c.tantiemes || 0), 0) || 1000}
              </td>
              <td className="border border-slate-300 px-3 py-2"></td>
              <td className="border border-slate-300 px-3 py-2"></td>
              <td className="border border-slate-300 px-3 py-2"></td>
            </tr>
          </tbody>
        </table>
        </div>
        <p className="text-xs text-slate-500 mt-2 italic">
          Quorum requis (majorité simple) : {Math.floor((coproprietaires.reduce((sum, c) => sum + (c.tantiemes || 0), 0) || 1000) / 2) + 1} tantièmes
        </p>
      </div>

      <div className="border-t-2 border-slate-300 my-6"></div>

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3">CLÔTURE DE L'ASSEMBLÉE</h3>
        <p className="text-sm mb-4">L'ordre du jour étant épuisé, la séance est levée à __h__.</p>
        <p className="text-sm mb-6">Le présent procès-verbal a été rédigé et signé par le Président et le Secrétaire de séance.</p>
        
        <div className="grid grid-cols-2 gap-8 mt-8">
          <div>
            <p className="text-sm mb-2">Le Président de séance,</p>
            <p className="text-xs text-slate-600 mb-10">{ag.president_seance || `${user.prenom} ${user.nom}`}</p>
            <div className="border-b border-slate-400 w-48"></div>
          </div>
          <div>
            <p className="text-sm mb-2">Le Secrétaire de séance,</p>
            <p className="text-xs text-slate-600 mb-10">{ag.secretaire || 'À désigner'}</p>
            <div className="border-b border-slate-400 w-48"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssembleesGeneralesV2;
